const amountMinor = (value) => {
  const text = String(value ?? "").trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(text)) throw new Error("Enter a valid refund amount.");
  const [whole, fraction = ""] = text.split(".");
  const minor = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  if (!Number.isSafeInteger(minor) || minor <= 0) throw new Error("Enter a valid refund amount.");
  return minor;
};

export const validateXeroRefundLines = (quote, amount, selections = []) => {
  if (!quote || !quote.xero_connected) return [];
  const requested = amountMinor(amount);
  const lines = Array.isArray(quote.lines) ? quote.lines : [];
  if (!lines.length) throw new Error("No refundable Xero invoice items are available.");
  const allocation = lines.length === 1 && (!Array.isArray(selections) || !selections.length)
    ? [{ line_item_id: lines[0].line_item_id, amount_minor: requested }]
    : selections;
  if (!Array.isArray(allocation) || !allocation.length) {
    throw new Error("Select invoice items for this Xero refund.");
  }
  const limits = new Map(lines.map((line) => [line.line_item_id, line.refundable_amount_minor]));
  const seen = new Set();
  let allocated = 0;
  for (const item of allocation) {
    if (!item || !limits.has(item.line_item_id) || seen.has(item.line_item_id)) {
      throw new Error("Select valid Xero invoice items once each.");
    }
    seen.add(item.line_item_id);
    if (!Number.isSafeInteger(item.amount_minor) || item.amount_minor <= 0) {
      throw new Error("Enter a positive amount for each selected invoice item.");
    }
    if (item.amount_minor > limits.get(item.line_item_id)) {
      throw new Error("The selected invoice item exceeds its available balance.");
    }
    allocated += item.amount_minor;
  }
  if (allocated !== requested) {
    throw new Error("Invoice item amounts must equal the refund amount.");
  }
  return allocation;
};

export const prepareXeroRefundValues = async (values, service) => {
  const selections = values.xero_line_allocations;
  if (Array.isArray(selections) && selections.length) {
    const seen = new Set();
    let selectedMinor = 0;
    for (const item of selections) {
      if (!item || typeof item.line_item_id !== "string" || !item.line_item_id
        || seen.has(item.line_item_id) || !Number.isSafeInteger(item.amount_minor)
        || item.amount_minor <= 0) {
        throw new Error("Select valid Xero invoice items once each.");
      }
      seen.add(item.line_item_id);
      selectedMinor += item.amount_minor;
    }
    if (selectedMinor !== amountMinor(values.total_deposit_refund)) {
      throw new Error("Invoice item amounts must equal the refund amount.");
    }
    // Flask rechecks invoice ownership and remaining capacity. A retry must
    // preserve the same selection even after the successful refund consumed it.
    return values;
  }
  let quote;
  try {
    const response = await service.refundXeroLines({
      sale_id: values.sale_id,
      client_id: values.client_id,
    });
    quote = response && response.data;
  } catch (_) {
    throw new Error("Unable to check Xero refund items. Please try again.");
  }
  if (!quote || typeof quote.xero_connected !== "boolean") {
    throw new Error("Unable to check Xero refund items. Please try again.");
  }
  if (!quote.xero_connected) {
    const ordinaryRefund = { ...values };
    delete ordinaryRefund["xero_line_allocations"];
    return ordinaryRefund;
  }
  return {
    ...values,
    xero_line_allocations: validateXeroRefundLines(
      quote, values.total_deposit_refund, values.xero_line_allocations,
    ),
  };
};
